import {
    EmptyTopBar,
    Loading,
    TwoColumnLayout,
} from '../../components';
import {useTranslation} from 'react-i18next';
import i18nNamespaces from '../../const/i18nNamespaces';
import {Box, Button} from '@material-ui/core';
import useStyles from './NewPrescriptionDraftPageStyles';
import {useContext, useEffect, useMemo, useState} from 'react';
import {PrescriptionDraft} from '../../types/prescription';
import {v4} from 'uuid';
import prescriptionsApi from '../../api/prescriptions/prescriptionsApi';
import {useHistory, useParams} from 'react-router';
import PrescriptionItem, {
    SelectedPrescriptionDraft,
} from '../../components/prescription/PrescriptionDraftItem/PrescriptionDraftItem';
import {
    mapPrescriptionToSendPrescriptionDraft,
} from '../../utils/prescription';
import {AlertContext} from '../../contexts/AlertContext';
import NewPrescriptionDraftForm from '../../components/prescription/NewPrescriptionDraftForm/NewPrescriptionDraftForm';
import { appointmentsApi } from '../../api/appointments/appointmentsApi';
import InsuranceDeclaration from '../../components/prescription/InsuranceDeclaration/InsuranceDeclaration';
import { insuranceDeclarationApi } from '../../api/insuranceDeclaration/insuranceDeclarationApi';
import {PatientInsuranceDeclarationDto} from '../../types/insuranceDeclaration';
import { useAppSelector } from '../../hooks/customReduxHooks';
import { selectAuthUserData } from '../../store/auth';


const NewPrescriptionDraftPage = () => {
    const {t} = useTranslation(i18nNamespaces.PRESCRIPTIONS);
    const {t: errorT} = useTranslation(i18nNamespaces.ERRORS);
    const classes = useStyles();
    const params = useParams<{appointmentId: string}>();
    const appointmentId = parseInt(params?.appointmentId);
    const authUserData = useAppSelector(selectAuthUserData);
    const history = useHistory();
    const [prescriptions, setPrescriptions] = useState<SelectedPrescriptionDraft[]>(
        [],
    );
    const [insuranceDeclaration, setInsuranceDeclaration] = useState<PatientInsuranceDeclarationDto | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const topbarTitle = t('prescriptionForm');
    const openAlert = useContext(AlertContext);
    const hasReimbursedPrescription = useMemo(
        () => prescriptions.some(p => p.medicationPack.isReimbursed === true),
        [prescriptions],
    );


    useEffect(() => {
        if (!hasReimbursedPrescription) {
            setInsuranceDeclaration(null);
        }
    }, [hasReimbursedPrescription]);

    const handleSubmitPrescriptionForm = (prescription: PrescriptionDraft) => {
        if(prescriptions.length >= 5){
            return openAlert({
                severity: 'error',
                message: t('maxPrescriptionsReached'),
            });
        }
        setPrescriptions(prev => [...prev, {...prescription, id: v4()}]);
    };

    const handleDeletePrescription = (prescriptionId: string) => {
        setPrescriptions(prev =>
            prev.filter(prescription => prescription.id !== prescriptionId),
        );
    };

    const handleSendPrescription = async () => {
        try {
            setLoading(true);
            if (!appointmentId || !authUserData?.userId) return;

            if (hasReimbursedPrescription) {
                if (!insuranceDeclaration) {
                    openAlert({
                        severity: 'error',
                        message: t('selectRegionalBranch'),
                    });
                    return;
                }

                const {data: insuranceDeclarationData} =
                    await insuranceDeclarationApi.sendInsuranceDeclaration(
                        insuranceDeclaration,
                        authUserData?.userId,
                    );

                if (!insuranceDeclarationData?.isSuccess) {
                    throw new Error(insuranceDeclarationData?.message);
                }
            }

            const {data} = await prescriptionsApi.createPrescriptionDraft({
                appointmentId,
                prescriptions: prescriptions.map(
                    mapPrescriptionToSendPrescriptionDraft,
                ),
            });

            // Temporary solution until backend creates prescription draft with "filled" status
            if (data.result)
                await appointmentsApi.confirmDraftPrescriptionFilled(appointmentId, data.result);

            if (data?.isSuccess)
                history.goBack();
            else throw new Error(data?.message);
        } catch (error) {
            openAlert({
                severity: 'error',
                message: errorT('unexpectedErrorOccurred'),
            });
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <TwoColumnLayout>
            <Loading withBackdrop loading={loading} />
            <TwoColumnLayout.Header>
                <EmptyTopBar title={topbarTitle} onCancel={() => history.goBack()} />
            </TwoColumnLayout.Header>
            <TwoColumnLayout.Main>
                <TwoColumnLayout.LeftColumn>
                    <Box className={classes.formContainer}>
                        <h2 style={{marginBottom: 0}}>
                            Lek {prescriptions.length + 1}
                        </h2>
                        <NewPrescriptionDraftForm
                            onSubmit={handleSubmitPrescriptionForm}
                        />
                    </Box>
                </TwoColumnLayout.LeftColumn>
                <TwoColumnLayout.RightColumn>
                    {prescriptions.length > 0 && (
                        <>
                            <Box className={classes.card}>
                                {prescriptions.map((prescription, idx) => (
                                    <PrescriptionItem
                                        key={prescription.id}
                                        prescription={prescription}
                                        index={idx}
                                        onDelete={handleDeletePrescription}
                                    />
                                ))}
                                {!hasReimbursedPrescription ? (
                                    <Button
                                        variant={'contained'}
                                        color={'secondary'}
                                        style={{marginTop: 10, fontWeight: 'bold'}}
                                        onClick={handleSendPrescription}
                                        fullWidth
                                    >
                                        {t('orderPrescription')}
                                    </Button>
                                ) : (
                                    <InsuranceDeclaration
                                        onDeclarationChange={setInsuranceDeclaration}
                                        submitButton={
                                            <Button
                                                variant={'contained'}
                                                color={'secondary'}
                                                style={{marginTop: 10, fontWeight: 'bold'}}
                                                onClick={handleSendPrescription}
                                                fullWidth
                                            >
                                                {t('orderPrescription')}
                                            </Button>
                                        }
                                    />
                                )}
                            </Box>
                        </>
                    )}
                </TwoColumnLayout.RightColumn>
            </TwoColumnLayout.Main>
        </TwoColumnLayout>
    );
};
export default NewPrescriptionDraftPage;
